import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Fuel, Camera, Plus, Gauge } from "lucide-react";

const money = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

async function fileToDataURL(file, maxKB = 900) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 1200 / Math.max(img.width, img.height));
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        let q = 0.85;
        let out = canvas.toDataURL("image/jpeg", q);
        while (out.length / 1024 > maxKB && q > 0.35) { q -= 0.1; out = canvas.toDataURL("image/jpeg", q); }
        resolve(out);
      };
      img.onerror = reject; img.src = reader.result;
    };
    reader.onerror = reject; reader.readAsDataURL(file);
  });
}

const EMPTY = { bowser_id: "", odometer_km: 0, litres_filled: 0, rate_per_litre: 0, total_cost: 0, fuel_station: "", payment_mode: "cash", odo_photo_url: "", receipt_photo_url: "", remarks: "" };

export default function DriverSelfFuel() {
  const qc = useQueryClient();
  const { data: bowsersData } = useQuery({ queryKey: ["driver-bowsers"], queryFn: () => api.get("/fleet/bowsers").then((r) => r.data) });
  const bowsers = bowsersData?.items || [];
  const { data } = useQuery({ queryKey: ["driver-fuel"], queryFn: () => api.get("/bowser-fuel").then((r) => r.data) });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const odoRef = useRef(null);
  const upd = (k) => (e) => {
    const val = e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e;
    const next = { ...form, [k]: val };
    if (k === "litres_filled" || k === "rate_per_litre") {
      next.total_cost = Number(((k === "litres_filled" ? val : form.litres_filled) * (k === "rate_per_litre" ? val : form.rate_per_litre) || 0).toFixed(2));
    }
    setForm(next);
  };
  const handleFile = async (kind, file) => {
    if (!file) return;
    try { const url = await fileToDataURL(file); setForm((f) => ({ ...f, [kind]: url })); }
    catch { toast.error("Could not read image"); }
  };

  const save = useMutation({
    mutationFn: () => api.post("/bowser-fuel", form),
    onSuccess: () => { toast.success("Fuel logged ✓"); qc.invalidateQueries({ queryKey: ["driver-fuel"] }); setOpen(false); setForm(EMPTY); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  const canSave = form.bowser_id && form.odometer_km > 0 && form.litres_filled > 0 && form.odo_photo_url;

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="driver-self-fuel-page">
      <div>
        <h1 className="font-display text-2xl font-black tracking-tight">Bowser Self-Fueling</h1>
        <p className="text-sm text-muted-foreground">Log every refuel at a pump for the bowser&rsquo;s own tank. Odometer reading + photo are mandatory.</p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="lg" className="pill-btn w-full" data-testid="btn-driver-new-fuel"><Plus size={16} className="mr-2" /> Log fuel now</Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Log bowser fuel</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Bowser <span className="text-status-cancelled">*</span></Label>
              <Select value={form.bowser_id} onValueChange={upd("bowser_id")}>
                <SelectTrigger data-testid="dsf-bowser"><SelectValue placeholder="Select bowser" /></SelectTrigger>
                <SelectContent>
                  {bowsers.map((b) => (<SelectItem key={b.id} value={b.id}>{b.code} — {b.registration_no}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Odometer (km) <span className="text-status-cancelled">*</span></Label><Input type="number" value={form.odometer_km || ""} onChange={upd("odometer_km")} data-testid="dsf-odo" /></div>
            <div><Label>Litres <span className="text-status-cancelled">*</span></Label><Input type="number" step="0.01" value={form.litres_filled || ""} onChange={upd("litres_filled")} data-testid="dsf-litres" /></div>
            <div><Label>Rate/L</Label><Input type="number" step="0.01" value={form.rate_per_litre || ""} onChange={upd("rate_per_litre")} /></div>
            <div><Label>Total (₹)</Label><Input type="number" step="0.01" value={form.total_cost || ""} onChange={upd("total_cost")} data-testid="dsf-total" /></div>
            <div className="col-span-2"><Label>Fuel station</Label><Input value={form.fuel_station} onChange={upd("fuel_station")} placeholder="IOCL / BPCL / HPCL name & location" /></div>
            <div className="col-span-2">
              <Label>Payment mode</Label>
              <Select value={form.payment_mode} onValueChange={upd("payment_mode")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="company_card">Company Card</SelectItem>
                  <SelectItem value="fastag">FASTag Fuel</SelectItem>
                  <SelectItem value="credit">Credit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 rounded-md border-2 border-dashed border-primary/40 bg-primary/5 p-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-bold text-primary"><Camera size={16} /> Odometer photo <span className="text-status-cancelled">*</span></div>
              <input ref={odoRef} type="file" accept="image/*" capture="environment" className="text-xs" onChange={(e) => handleFile("odo_photo_url", e.target.files?.[0])} data-testid="dsf-odo-file" />
              {form.odo_photo_url && <img src={form.odo_photo_url} alt="odo" className="mt-2 max-h-40 rounded border border-border" />}
            </div>
            <div className="col-span-2"><Label>Bill photo (optional)</Label>
              <input type="file" accept="image/*" className="text-xs" onChange={(e) => handleFile("receipt_photo_url", e.target.files?.[0])} />
              {form.receipt_photo_url && <img src={form.receipt_photo_url} alt="rcpt" className="mt-2 max-h-32 rounded border border-border" />}
            </div>
            <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={upd("remarks")} /></div>
          </div>
          <DialogFooter><Button onClick={() => save.mutate()} disabled={!canSave || save.isPending} data-testid="btn-driver-save-fuel">{save.isPending ? "Saving…" : "Log fuel"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Gauge size={18} /> My recent fuel logs</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(data?.items || []).length === 0 && <div className="py-6 text-center text-sm text-muted-foreground"><Fuel size={28} className="mx-auto mb-2 opacity-40" />No fuel logs yet.</div>}
          {(data?.items || []).map((l) => (
            <div key={l.id} className="flex items-center gap-3 rounded-lg border border-border/50 p-3" data-testid={`drv-fuel-${l.code}`}>
              {l.odo_photo_url && <img src={l.odo_photo_url} alt="odo" className="h-14 w-14 rounded object-cover" />}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-sm font-bold">{l.code}</div>
                  <div className="font-mono text-sm font-bold">{money(l.total_cost)}</div>
                </div>
                <div className="text-xs text-muted-foreground">{l.bowser_code} · {l.date?.slice(0, 10)}</div>
                <div className="mt-1 flex items-center gap-3 text-xs">
                  <span>Odo: <b className="font-mono">{l.odometer_km?.toLocaleString()}</b></span>
                  <span>{l.litres_filled}L @ ₹{l.rate_per_litre}</span>
                  {l.mileage_kmpl > 0 && <span className="text-primary">{l.mileage_kmpl.toFixed(2)} km/L</span>}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
